/**
 * Anomaly Score Bar Renderer
 * 
 * Renders bar chart showing anomaly scores for different items.
 * Color-codes bars based on anomaly level.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderAnomalyScoreBar: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { label: string, score: number, threshold?: number }
  // Or: { items: Array<{ name: string, anomalyScore: number }>, threshold?: number }
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  const items = data.map(item => ({
    label: item.label || item.name || 'Item',
    score: Number(item.score !== undefined ? item.score : item.anomalyScore) || 0,
    threshold: Number(item.threshold) || 0.7 // Default threshold
  }));

  // Use threshold from first item or meta
  const threshold = items[0]?.threshold || meta?.confidence || 0.7;

  // Sort by score (descending)
  items.sort((a, b) => b.score - a.score);

  // Calculate dimensions
  const width = 600;
  const barHeight = 30;
  const barSpacing = 10;
  const padding = { top: 20, right: 100, bottom: 20, left: 150 };
  const chartHeight = items.length * (barHeight + barSpacing) + padding.top + padding.bottom;
  const chartWidth = width - padding.left - padding.right;

  // Find max score for scaling
  const maxScore = Math.max(...items.map(i => i.score), threshold, 1);

  // Generate bars
  const barElements = items.map((item, index) => {
    const barWidth = (item.score / maxScore) * chartWidth;
    const y = padding.top + (index * (barHeight + barSpacing));
    
    // Determine color based on score vs threshold
    let barColor = 'rgba(100, 200, 100, 0.6)'; // Low anomaly (green)
    let barStroke = 'rgba(100, 200, 100, 0.9)';
    
    if (item.score >= threshold) {
      barColor = 'rgba(255, 100, 100, 0.8)'; // High anomaly (red)
      barStroke = 'rgba(255, 100, 100, 1)';
    } else if (item.score >= threshold * 0.7) {
      barColor = 'rgba(255, 200, 100, 0.7)'; // Medium anomaly (yellow)
      barStroke = 'rgba(255, 200, 100, 0.9)';
    }

    // Truncate long labels
    const displayLabel = item.label.length > 25 
      ? item.label.substring(0, 25) + '...' 
      : item.label;

    // Threshold indicator line
    const thresholdX = padding.left + (threshold / maxScore) * chartWidth;
    const thresholdLine = index === 0 ? `
      <line
        x1="${thresholdX}"
        y1="${padding.top - 5}"
        x2="${thresholdX}"
        y2="${chartHeight - padding.bottom}"
        stroke="rgba(255, 200, 100, 0.5)"
        stroke-width="2"
        stroke-dasharray="5,5"
        opacity="0.6"
      />
      <text
        x="${thresholdX + 5}"
        y="${padding.top + 10}"
        font-size="${designTokens.fontSize.xs}"
        fill="rgba(255, 200, 100, 0.9)"
        font-weight="600"
      >Threshold: ${threshold.toFixed(2)}</text>
    ` : '';

    return `
      <g>
        ${thresholdLine}
        <rect
          x="${padding.left}"
          y="${y}"
          width="${barWidth}"
          height="${barHeight}"
          fill="${barColor}"
          stroke="${barStroke}"
          stroke-width="1"
          rx="4"
          style="cursor: pointer; transition: opacity 0.2s;"
          onmouseenter="this.style.opacity='0.9';"
          onmouseleave="this.style.opacity='1';"
        />
        <text
          x="${padding.left - 10}"
          y="${y + barHeight / 2 + 4}"
          text-anchor="end"
          font-size="${designTokens.fontSize.sm}"
          fill="${designTokens.contentColor}"
        >${escapeHtml(displayLabel)}</text>
        <text
          x="${padding.left + barWidth + 10}"
          y="${y + barHeight / 2 + 4}"
          font-size="${designTokens.fontSize.sm}"
          fill="${designTokens.contentColor}"
          font-weight="600"
        >${item.score.toFixed(3)}</text>
        ${item.score >= threshold ? `
          <text
            x="${padding.left + barWidth / 2}"
            y="${y + barHeight / 2 + 4}"
            text-anchor="middle"
            font-size="${designTokens.fontSize.xs}"
            fill="rgba(255, 255, 255, 0.9)"
            font-weight="600"
          >⚠</text>
        ` : ''}
      </g>
    `;
  }).join('');

  const title = meta?.title || 'Anomaly Score Bar';
  const anomalyCount = items.filter(i => i.score >= threshold).length;

  return `
    <div style="width: 100%;">
      ${title ? `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          font-size: ${designTokens.fontSize.md};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">
          ${escapeHtml(title)}
          ${anomalyCount > 0 ? `
            <span style="
              margin-left: ${designTokens.padding.sm};
              font-size: ${designTokens.fontSize.sm};
              color: rgba(255, 100, 100, 0.9);
            ">(${anomalyCount} anomal${anomalyCount !== 1 ? 'ies' : 'y'})</span>
          ` : ''}
        </div>
      ` : ''}
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${width}"
          height="${chartHeight}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${barElements}
        </svg>
      </div>
      <div style="
        margin-top: ${designTokens.padding.sm};
        font-size: ${designTokens.fontSize.xs};
        color: ${designTokens.contentColorMuted};
        text-align: center;
      ">
        Threshold: ${threshold.toFixed(2)} | 
        <span style="color: rgba(100, 200, 100, 0.9);">Low</span> | 
        <span style="color: rgba(255, 200, 100, 0.9);">Medium</span> | 
        <span style="color: rgba(255, 100, 100, 0.9);">High</span>
      </div>
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


